import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cerrarSesion, leerSesion, mensajeDeError, misTramites, resumenDeTramites } from '../api';
import { useTema } from '../tema';
import BotonTema from '../componentes/BotonTema';
import ModalArchivos from '../componentes/ModalArchivos';
import ModalHistorial from '../componentes/ModalHistorial';
import ActivarAvisos from '../componentes/ActivarAvisos';

const POR_PAGINA = 20;

/** Dónde se recuerda en qué estado quedó cada trámite la última vez que este cliente miró. */
const LLAVE_VISTOS = 'portal-wf-estados-vistos';

const fmtFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

/**
 * Verde solo para el trámite REALMENTE terminado; ámbar para lo que va andando.
 *
 * El verde estaba mal repartido: se pintaba cualquier estado que dijera "terminado", así que
 * "Previo terminado" salía verde como si el trámite entero estuviera listo — y no lo está, le
 * falta todo lo de después. "Trámite facturado" tampoco: le falta la revisión.
 *
 * Verde es exactamente el último escalón, el mismo corte que usan los contadores de arriba y el
 * filtro. Si no coincidieran, el cliente vería tres verdes en la tabla y un contador diciendo
 * uno.
 */
const esTerminado = (estado) => (estado || '').toLowerCase().trim() === 'trámite terminado';

const claseEstado = (estado) => {
  if (esTerminado(estado)) return 'estado estado-terminado';
  const texto = (estado || '').toLowerCase();
  if (texto.includes('proceso') || texto.includes('permisos')) return 'estado estado-proceso';
  return 'estado';
};

const leerVistos = () => {
  try { return JSON.parse(localStorage.getItem(LLAVE_VISTOS) || '{}'); } catch { return {}; }
};
const guardarVistos = (mapa) => {
  try { localStorage.setItem(LLAVE_VISTOS, JSON.stringify(mapa)); } catch { /* preferencia */ }
};

/**
 * Un número grande de los de arriba, que además filtra al tocarlo.
 *
 * Los colores se pasan por parámetro y salen de las variables del sistema: el ámbar de "en
 * proceso" y el verde de "terminados" son exactamente los mismos de las etiquetas de la tabla,
 * para que el ojo ate el número con las filas sin tener que leer.
 */
function Contador({ etiqueta, valor, color, fondo, activo, alTocar }) {
  return (
    <button
      type="button"
      onClick={alTocar}
      aria-pressed={activo}
      title={activo ? 'Tocar para ver todos' : `Ver solo: ${etiqueta.toLowerCase()}`}
      style={{
        textAlign: 'left', cursor: 'pointer', padding: '14px 16px', borderRadius: 12,
        background: activo ? fondo : 'var(--blanco)',
        border: `1.5px solid ${activo ? color : 'var(--gris-borde)'}`,
        boxShadow: 'var(--sombra)',
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color }}>{valor}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--gris-texto)', marginTop: 2 }}>
        {etiqueta}
      </div>
    </button>
  );
}

/**
 * La pantalla del portal. Una sola, como se pidió.
 *
 * Arriba el buscador por documento asociado; debajo la tabla con el estado de cada trámite y
 * los botones para ver los archivos y la factura.
 *
 * La campanita marca los trámites que CAMBIARON de estado desde la última vez que este cliente
 * entró. El "visto" se guarda en su navegador, no en su cuenta, y es a propósito: sirve para lo
 * que se pidió —que se dé cuenta de lo que cambió sin tener que repasar la lista— sin
 * inventarle a WF una tabla de lecturas por persona que después hay que mantener. Lo que no
 * hace, y conviene decirlo claro, es avisar con el portal cerrado.
 */
export default function MisTramites() {
  const navegar = useNavigate();
  const { tema, alternar } = useTema();
  const sesion = leerSesion();

  /**
   * El buscador arranca con lo que venga en la direccion (?buscar=...).
   *
   * Es lo que hace util el aviso: al tocarlo, el portal abre ya con ESE tramite en pantalla y
   * no con la lista completa —CAPRIS tiene 110— donde habria que ir a encontrarlo a mano.
   */
  const [buscar, setBuscar] = useState(
    () => new URLSearchParams(window.location.search).get('buscar') || ''
  );
  const [pagina, setPagina] = useState(0);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [archivosDe, setArchivosDe] = useState(null); // { tramite, clase }
  // El recorrido completo del tramite, que se abre con "Ver detalle". Ver ModalHistorial.
  const [historialDe, setHistorialDe] = useState(null);
  const [cambiados, setCambiados] = useState([]);
  const [verCambios, setVerCambios] = useState(false);
  const [mensaje, setMensaje] = useState(null);   // { texto, clase }
  const [resumen, setResumen] = useState(null);  // { total, enProceso, terminados }
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  // null = todos | false = solo los que siguen | true = solo los terminados
  const [soloTerminados, setSoloTerminados] = useState(null);

  // El buscador espera a que la persona deje de escribir: sin esto se dispara una consulta por
  // cada tecla y la lista parpadea mientras se teclea.
  const [textoBuscado, setTextoBuscado] = useState(
    () => new URLSearchParams(window.location.search).get('buscar') || ''
  );
  const temporizador = useRef(null);
  useEffect(() => {
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => {
      setTextoBuscado(buscar.trim());
      setPagina(0);
    }, 350);
    return () => clearTimeout(temporizador.current);
  }, [buscar]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const { data } = await misTramites({
        buscar: textoBuscado, desde, hasta,
        terminado: soloTerminados, pagina, porPagina: POR_PAGINA,
      });
      setDatos(data);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudieron cargar sus trámites.'));
    } finally {
      setCargando(false);
    }
  }, [textoBuscado, desde, hasta, soloTerminados, pagina]);

  useEffect(() => { cargar(); }, [cargar]);

  // Al cambiar cualquier filtro se vuelve a la primera página: quedarse en la 4 de una lista
  // que ahora tiene dos páginas deja la pantalla en blanco sin explicación.
  useEffect(() => { setPagina(0); }, [desde, hasta, soloTerminados]);

  /**
   * Los contadores de arriba. Se piden UNA vez y no se recalculan al filtrar.
   *
   * Son el panorama de la cuenta: cuántos trámites tiene y cuántos ya terminaron. Si cambiaran
   * con cada filtro dejarían de ser una referencia y pasarían a repetir lo que la tabla ya dice.
   */
  useEffect(() => {
    resumenDeTramites()
      .then(({ data }) => setResumen(data))
      .catch(() => {});   // es un extra: si falla, la tabla de abajo igual avisa
  }, []);

  /**
   * Qué cambió desde la última visita. Se calcula UNA vez al abrir el portal.
   *
   * Va aparte de cargar() por un motivo que costó verlo: comparar y guardar en el mismo sitio
   * hace que la comparación se coma sus propias novedades. Al montar, React llama al efecto dos
   * veces en desarrollo; la primera guardaba los estados nuevos y la segunda ya no encontraba
   * ninguna diferencia. Y con el portal en la calle habría pasado igual apenas alguien buscara
   * o pasara de página: cada carga machacaba el "visto" y los cambios desaparecían sin que
   * nadie los llegara a ver.
   *
   * Así que esto corre una sola vez, con la primera página -que es la de los trámites más
   * recientes, donde están los cambios que importan- y solo entonces mueve el "visto".
   */
  const yaSeRevisaron = useRef(false);
  useEffect(() => {
    if (yaSeRevisaron.current) return;
    yaSeRevisaron.current = true;

    misTramites({ pagina: 0, porPagina: POR_PAGINA })
      .then(({ data }) => {
        const vistos = leerVistos();
        const novedades = [];
        // Se MEZCLA con lo que ya había: si solo se guardara esta página, los trámites viejos
        // perderían su estado guardado y volverían a contarse como novedad más adelante.
        const actualizado = { ...vistos };
        (data.content || []).forEach((t) => {
          if (vistos[t.id] && vistos[t.id] !== t.estado) {
            novedades.push({ ...t, estadoAnterior: vistos[t.id] });
          }
          actualizado[t.id] = t.estado;
        });
        setCambiados(novedades);
        guardarVistos(actualizado);
      })
      // Si esto falla no se le dice nada: es un extra, y la tabla de abajo ya avisa si el
      // problema es de verdad.
      .catch(() => {});
  }, []);

  const salir = () => {
    cerrarSesion();
    navegar('/ingresar', { replace: true });
  };

  const hayFiltro = !!(buscar || desde || hasta || soloTerminados !== null);

  const limpiarFiltros = () => {
    setBuscar('');
    setDesde('');
    setHasta('');
    setSoloTerminados(null);
  };

  const totalPaginas = datos ? datos.totalPages : 0;
  const filas = datos?.content || [];
  const idsCambiados = new Set(cambiados.map((c) => c.id));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gris-claro)' }}>

      {/* ── Barra superior — el mismo azul y el mismo logo del sistema ──── */}
      <header className="navbar">
        <img
          src="/LOGO-WF-long-color.png"
          alt="WF Aduanas y Logística"
          style={{ height: 34, width: 'auto', flexShrink: 0 }}
        />
        <div className="navbar-empresa">
          <div style={{
            fontSize: 13, fontWeight: 700, color: '#ffffff',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {sesion?.empresa}
          </div>
          {/* La bajada sobra en el teléfono: la pantalla YA es el portal, y ahí el ancho hace
              falta para que el nombre de la empresa se lea entero. */}
          <div className="solo-ancho" style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)' }}>
            Portal de clientes
          </div>
        </div>

        <button
          type="button"
          onClick={() => setVerCambios((v) => !v)}
          title={cambiados.length ? `${cambiados.length} trámite(s) cambiaron de estado` : 'Sin cambios nuevos'}
          aria-label="Cambios desde su última visita"
          className="btn-barra"
          style={{ position: 'relative', padding: '7px 10px', fontSize: 15 }}
        >
          🔔
          {cambiados.length > 0 && (
            <span style={{
              position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, padding: '0 5px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800, borderRadius: 9,
              color: '#ffffff', background: '#dc2626',
            }}>{cambiados.length}</span>
          )}
        </button>

        <ActivarAvisos alAvisar={(texto, clase) => setMensaje({ texto, clase })} />

        <BotonTema tema={tema} alternar={alternar} variante="barra" />

        <button type="button" className="btn-barra" onClick={salir}>Salir</button>
      </header>

      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 16px 40px' }}>

        {mensaje && (
          <div className={`alert ${mensaje.clase === 'error' ? 'alert-error' : 'alert-success'}`}
            style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ flex: 1 }}>{mensaje.texto}</span>
            <button type="button" onClick={() => setMensaje(null)} aria-label="Cerrar aviso"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit',
                       fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
          </div>
        )}

        {verCambios && (
          <div className="tarjeta" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>
              Cambios desde su última visita
            </div>
            {cambiados.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--gris-texto)' }}>
                Nada cambió desde la última vez que entró.
              </div>
            ) : (
              cambiados.map((t) => (
                <div key={t.id} style={{
                  padding: '9px 0', borderTop: '1px solid var(--gris-borde)', fontSize: 13,
                }}>
                  <strong>{t.documentoAsociado}</strong>
                  <span style={{ color: 'var(--gris-texto)' }}> · {t.estadoAnterior} → </span>
                  <strong>{t.estado}</strong>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Los dos contadores ───────────────────────────────────────────
            Son botones, no cartelitos: el número que llama la atención es también la
            forma de ver esos trámites. Tocar el que ya está puesto lo quita. */}
        {resumen && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 12, marginBottom: 16,
          }}>
            <Contador
              etiqueta="Trámites en proceso"
              valor={resumen.enProceso}
              color="var(--sobre-tinte-ambar)"
              fondo="var(--tinte-ambar)"
              activo={soloTerminados === false}
              alTocar={() => setSoloTerminados(soloTerminados === false ? null : false)}
            />
            <Contador
              etiqueta="Trámites terminados"
              valor={resumen.terminados}
              color="var(--sobre-tinte-verde)"
              fondo="var(--verde-claro)"
              activo={soloTerminados === true}
              alTocar={() => setSoloTerminados(soloTerminados === true ? null : true)}
            />
          </div>
        )}

        {/* ── Buscador y filtros ───────────────────────────────────────── */}
        <div className="tarjeta" style={{ padding: 14, marginBottom: 16 }}>
          <input
            className="form-control"
            type="search"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Buscar por su número de documento…"
            aria-label="Buscar por documento asociado"
          />

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10, marginTop: 12,
          }}>
            <div>
              <label className="form-label" htmlFor="desde">Recibido desde</label>
              <input id="desde" className="form-control" type="date"
                value={desde} max={hasta || undefined}
                onChange={(e) => setDesde(e.target.value)} />
            </div>
            <div>
              <label className="form-label" htmlFor="hasta">Recibido hasta</label>
              <input id="hasta" className="form-control" type="date"
                value={hasta} min={desde || undefined}
                onChange={(e) => setHasta(e.target.value)} />
            </div>
            <div>
              <label className="form-label" htmlFor="estado">Estado</label>
              {/* El desplegable y los contadores mueven el MISMO filtro: si fueran dos cosas
                  distintas, tocar uno dejaría al otro mostrando algo que ya no es cierto. */}
              <select id="estado" className="form-control"
                value={soloTerminados === null ? '' : String(soloTerminados)}
                onChange={(e) => setSoloTerminados(
                  e.target.value === '' ? null : e.target.value === 'true')}>
                <option value="">Todos</option>
                <option value="false">En proceso</option>
                <option value="true">Terminados</option>
              </select>
            </div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, color: 'var(--gris-texto)' }}>
              {datos ? `${datos.totalElements} trámite${datos.totalElements === 1 ? '' : 's'}` : ''}
              {hayFiltro ? ' con estos filtros' : ''}
            </span>
            {hayFiltro && (
              <button type="button" className="btn-enlace" onClick={limpiarFiltros}>
                Quitar filtros
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}

        {/* ── Tabla ────────────────────────────────────────────────────── */}
        <div className="tarjeta tabla-scroll">
          <table className="tabla">
            <thead>
              <tr>
                <th>Documento</th>
                <th className="solo-ancho">Recibido</th>
                <th>Estado</th>
                <th style={{ textAlign: 'right' }}>Archivos</th>
              </tr>
            </thead>
            <tbody>
              {cargando && (
                <tr><td colSpan={4} style={{ padding: 28, textAlign: 'center', color: 'var(--gris-texto)' }}>
                  Cargando…
                </td></tr>
              )}

              {!cargando && filas.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 28, textAlign: 'center', color: 'var(--gris-texto)' }}>
                  {textoBuscado
                    ? `No encontramos ningún trámite con "${textoBuscado}".`
                    : 'Todavía no hay trámites para mostrar.'}
                </td></tr>
              )}

              {!cargando && filas.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                      {t.documentoAsociado}
                      {idsCambiados.has(t.id) && (
                        <span title="Cambió desde su última visita" style={{
                          marginLeft: 7, fontSize: 10.5, fontWeight: 800, padding: '2px 7px',
                          borderRadius: 20, color: '#fff', background: '#dc2626',
                        }}>NUEVO</span>
                      )}
                    </div>
                    <div className="solo-ancho-inverso" style={{ fontSize: 12, color: 'var(--gris-texto)' }}>
                      {fmtFecha(t.fechaRecibido)}
                    </div>
                  </td>
                  <td className="solo-ancho" style={{ fontSize: 13, color: 'var(--gris-texto)', whiteSpace: 'nowrap' }}>
                    {fmtFecha(t.fechaRecibido)}
                  </td>
                  <td>
                    {/* El estado dice donde va HOY; "Ver detalle" abre todo el recorrido, que es
                        lo que evita la llamada de "y antes que paso". Va A LA DERECHA del estado y
                        no debajo: debajo quedaba como un enlace suelto que nunca se alineaba con
                        estados de largo distinto. */}
                    <div className="estado-con-detalle">
                      <span className={claseEstado(t.estado)}>{t.estado}</span>
                      <button
                        type="button"
                        className="boton-ver-detalle"
                        onClick={() => setHistorialDe(t)}
                        title="Ver todos los estados por los que pasó este trámite"
                      >
                        Ver detalle <span aria-hidden="true">›</span>
                      </button>
                    </div>
                  </td>
                  {/* Los dos botones llevan su NOMBRE y su color, no solo un dibujito: con dos
                      iconos parecidos (📄 y 🧾) no había forma de saber cuál era cuál sin probar
                      —lo reportó Axel—. El de documentos además dice cuántos hay. */}
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button
                        type="button" className="btn btn-archivos"
                        onClick={() => setArchivosDe({ tramite: t, clase: 'documentos' })}
                        disabled={t.documentos === 0}
                        title={t.documentos === 0 ? 'Todavía no hay documentos' : 'Ver los documentos del trámite'}
                      >
                        📄 Documentos{t.documentos > 0 ? ` (${t.documentos})` : ''}
                      </button>
                      <button
                        type="button" className="btn btn-factura"
                        onClick={() => setArchivosDe({ tramite: t, clase: 'factura' })}
                        disabled={!t.tieneFactura}
                        title={t.tieneFactura ? 'Ver la factura' : 'Todavía no hay factura'}
                      >
                        🧾 Factura
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Paginación ───────────────────────────────────────────────── */}
        {totalPaginas > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16,
          }}>
            <button type="button" className="btn btn-secondary"
              onClick={() => setPagina((p) => Math.max(p - 1, 0))} disabled={pagina === 0}
              style={{ padding: '8px 14px', fontSize: 13 }}>
              ← Anterior
            </button>
            <span style={{ fontSize: 13, color: 'var(--gris-texto)' }}>
              Página {pagina + 1} de {totalPaginas}
            </span>
            <button type="button" className="btn btn-secondary"
              onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas - 1))}
              disabled={pagina >= totalPaginas - 1}
              style={{ padding: '8px 14px', fontSize: 13 }}>
              Siguiente →
            </button>
          </div>
        )}
      </main>

      {archivosDe && (
        <ModalArchivos
          tramite={archivosDe.tramite}
          clase={archivosDe.clase}
          alCerrar={() => setArchivosDe(null)}
        />
      )}

      {historialDe && (
        <ModalHistorial tramite={historialDe} alCerrar={() => setHistorialDe(null)} />
      )}
    </div>
  );
}
